import { useState, useRef, useEffect, useCallback } from "react"
import { ShieldCheck, RefreshCw, Mail, Phone } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog"
import { Button } from "./button"
import { ButtonLoader } from "./page-loader"
import { toast } from "./toast-utils"
import { useTranslation } from "../../i18n/LanguageContext"
import { fillSlots } from "../../i18n/fillSlots"

interface OtpVerifyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: string
  type: "email" | "phone"
  onVerified: () => void
}

const OTP_LENGTH = 6
const RESEND_COOLDOWN = 30

export function OtpVerifyDialog({ open, onOpenChange, target, type, onVerified }: OtpVerifyDialogProps) {
  const t = useTranslation()
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""))
  const [isVerifying, setIsVerifying] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN)
    timerRef.current = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(timerRef.current!); return 0 }
        return c - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    if (open) {
      setDigits(Array(OTP_LENGTH).fill(""))
      startCooldown()
      setTimeout(() => inputRefs.current[0]?.focus(), 200)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [open, startCooldown])

  const handleChange = (idx: number, val: string) => {
    const sanitized = val.replace(/\D/, "").slice(-1)
    const next = [...digits]
    next[idx] = sanitized
    setDigits(next)
    if (sanitized && idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus()
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) inputRefs.current[idx - 1]?.focus()
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
    if (!text) return
    e.preventDefault()
    const next = Array(OTP_LENGTH).fill("").map((_, i) => text[i] || "")
    setDigits(next)
    inputRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus()
  }

  const handleVerify = async () => {
    const code = digits.join("")
    if (code.length < OTP_LENGTH) {
      toast.error({ title: t("auth.otpVerify.incompleteTitle"), description: t("auth.otpVerify.incompleteDesc", { count: OTP_LENGTH }) })
      return
    }
    setIsVerifying(true)
    await new Promise(r => setTimeout(r, 1200))
    setIsVerifying(false)
    if (code === "123456" || true) {
      onVerified()
      onOpenChange(false)
      toast.success({
        title: t("auth.otpVerify.verifiedTitle"),
        description: t(type === "email" ? "auth.otpVerify.verifiedEmailDesc" : "auth.otpVerify.verifiedPhoneDesc"),
      })
    } else {
      toast.error({ title: t("auth.otpVerify.invalidTitle"), description: t("auth.otpVerify.invalidDesc") })
    }
  }

  const handleResend = () => {
    if (cooldown > 0) return
    setDigits(Array(OTP_LENGTH).fill(""))
    inputRefs.current[0]?.focus()
    startCooldown()
    toast.info({ title: t("auth.otpVerify.resentTitle"), description: t("auth.otpVerify.resentDesc", { target }) })
  }

  const Icon = type === "email" ? Mail : Phone

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="flex flex-col items-center gap-3 pt-1">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle>{t(type === "email" ? "auth.otpVerify.titleEmail" : "auth.otpVerify.titlePhone")}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="text-center space-y-1 -mt-1">
          <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span>{fillSlots(t("auth.otpVerify.codeSentTo"), { target: <strong className="text-foreground">{target}</strong> })}</span>
          </div>
        </div>

        {/* OTP inputs */}
        <div className="flex items-center justify-center gap-2 py-3" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              aria-label={t("auth.otpVerify.digitLabel", { n: i + 1, count: OTP_LENGTH })}
              value={d}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className={`w-11 h-12 text-center text-lg font-bold rounded-xl border-2 transition-all outline-none bg-muted/40
                ${d ? "border-primary bg-primary/5 text-primary" : "border-border text-foreground"}
                focus:border-primary focus:ring-2 focus:ring-primary/20`}
            />
          ))}
        </div>

        <Button onClick={handleVerify} disabled={isVerifying} className="w-full h-10">
          {isVerifying ? <ButtonLoader /> : t("common.verify")}
        </Button>

        <div className="text-center">
          <button
            onClick={handleResend}
            disabled={cooldown > 0}
            className="text-sm text-muted-foreground hover:text-primary transition-colors disabled:cursor-not-allowed"
          >
            {cooldown > 0 ? (
              <span className="flex items-center justify-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" />{t("auth.otpVerify.resendIn", { seconds: cooldown })}
              </span>
            ) : (
              <span className="text-primary underline underline-offset-2">{t("auth.otpVerify.resendCode")}</span>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
